import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { EmblaOptionsType } from 'embla-carousel';
import { useEmblaCarousel } from 'embla-carousel/react';
import classNames from 'classnames';

import { IconChevronLeft, IconChevronRight } from 'icons';

import { CarouselProvider, useCarousel } from './context';
import styles from './Carousel.module.css';

type CarouselProps = {
  children: ReactNode;
  className?: string;
  onChangeScrollProgress?: (progress: number) => void;
} & Partial<EmblaOptionsType>;

type CarouselSlideProps = Omit<CarouselProps, 'buttons'>;
type CarouselContainerProps = Omit<CarouselProps, 'buttons'>;

type CarouselButtonProps = {
  direction: 'next' | 'prev';
};

type CarouselProgressProps = {
  progress: number;
};

type ControlButtonType = Omit<CarouselButtonProps, 'direction'>;

type CarouselType = FC<CarouselProps> & {
  Slide: FC<CarouselSlideProps>;
  Container: FC<CarouselContainerProps>;
  PrevButton: FC<ControlButtonType>;
  NextButton: FC<ControlButtonType>;
  Progress: FC<CarouselProgressProps>;
};

export const Carousel: CarouselType = props => {
  const { children, className, onChangeScrollProgress, ...options } = props;

  const [ref, carousel] = useEmblaCarousel({
    containScroll: 'trimSnaps',
    dragFree: true,
    draggableClass: styles.is_draggable,
    draggingClass: styles.is_dragging,
    ...options,
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const onSelect = useCallback(() => {
    if (!carousel) return;
    setPrevBtnEnabled(carousel.canScrollPrev());
    setNextBtnEnabled(carousel.canScrollNext());
  }, [carousel]);

  const onScroll = useCallback(() => {
    if (!carousel) return;

    const progress = Math.max(0, Math.min(1, carousel.scrollProgress()));

    if (onChangeScrollProgress) {
      onChangeScrollProgress(progress);
    }
  }, [carousel, onChangeScrollProgress]);

  useEffect(() => {
    if (!carousel) return;
    carousel.on('select', onSelect);
    carousel.on('scroll', onScroll);
    onSelect();
  }, [carousel, onSelect, onScroll]);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <CarouselProvider
        canScrollNext={nextBtnEnabled}
        canScrollPrev={prevBtnEnabled}
        carousel={carousel}
      >
        <div ref={ref} className={styles.viewport}>
          {children}
        </div>
        <Carousel.PrevButton />
        <Carousel.NextButton />
      </CarouselProvider>
    </div>
  );
};

const CarouselSlide: FC<CarouselSlideProps> = ({ children, className }) => {
  return <div className={className}>{children}</div>;
};

const CarouselContainer: FC<CarouselContainerProps> = ({ children }) => {
  return <div className={styles.container}>{children}</div>;
};

const CarouselButton: FC<CarouselButtonProps> = props => {
  const { direction } = props;
  const { carousel, canScrollPrev, canScrollNext } = useCarousel();

  const enabled = direction === 'next' ? canScrollNext : canScrollPrev;

  const handlerScrollClick = useCallback(() => {
    if (!carousel) return;

    if (direction === 'next') {
      carousel.scrollNext();
      return;
    }

    if (direction === 'prev') {
      carousel.scrollPrev();
    }
  }, [carousel, direction]);

  const Icon = direction === 'prev' ? IconChevronLeft : IconChevronRight;

  const directionClasses =
    direction === 'prev' ? styles.prev_button : styles.next_button;

  if (!enabled) return null;
  return (
    <button
      type="button"
      onClick={handlerScrollClick}
      className={classNames(styles.button, directionClasses)}
    >
      <Icon size="l" style={{ color: '#fff' }} />
    </button>
  );
};

const CarouselProgress: FC<CarouselProgressProps> = props => {
  const { progress } = props;

  return (
    <div className={styles.progress_wrapper}>
      <div
        className={styles.progress_bar}
        style={{ transform: `translateX(${progress * 100}%)` }}
      />
    </div>
  );
};

const NextButton: FC<ControlButtonType> = props => {
  return <CarouselButton {...props} direction="next" />;
};

const PrevButton: FC<ControlButtonType> = props => {
  return <CarouselButton {...props} direction="prev" />;
};

Carousel.Slide = CarouselSlide;
Carousel.Container = CarouselContainer;
Carousel.NextButton = NextButton;
Carousel.PrevButton = PrevButton;
Carousel.Progress = CarouselProgress;
